import { useState, useEffect } from "react";
import {
  Box,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Icon,
  makeStyles,
  InputAdornment,
  TextField,
  CircularProgress,
  TablePagination,
  Typography,
  Button,
  Chip,
} from "@material-ui/core";
import SearchRoundedIcon from "@material-ui/icons/SearchRounded";
import { useDebounce } from "../../hooks/useDebounce";
import CheckCircleOutlineRoundedIcon from "@material-ui/icons/CheckCircleOutlineRounded";
import SettingsBackupRestoreOutlinedIcon from "@material-ui/icons/SettingsBackupRestoreOutlined";

export default function SampleList({
  products,
  limit,
  page,
  changePaginationProducts,
  count,
  loading,
  productsSelected,
  changeProductsSelected,
  restoreProductsSelected,
}) {
  const classes = useStyles();
  const [localRegex, setLocalRegex] = useState("");
  const regexDebounced = useDebounce(localRegex, 500);

  useEffect(() => {
    changePaginationProducts({
      action: "QUERY",
      data: {
        query: regexDebounced,
      },
    });
  }, [regexDebounced]);

  return (
    <>
      <Box
        display={"flex"}
        style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 8 }}
      >
        <TextField
          disabled={loading}
          value={localRegex}
          variant="outlined"
          size="small"
          placeholder="name..."
          onChange={(e) => setLocalRegex(e.target.value)}
          fullWidth
          className={classes.searchBar}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchRoundedIcon />
              </InputAdornment>
            ),
          }}
        />
        {!!Object.keys(productsSelected || {}).length && (
          <Button
            color="primary"
            variant="outlined"
            className={classes.restore_selection}
            onClick={restoreProductsSelected}
          >
            <Icon style={{ display: "flex" }}>
              <SettingsBackupRestoreOutlinedIcon />
            </Icon>
            <Typography
              component={"span"}
              data-label="label"
              style={{ lineHeight: 1.1 }}
            >
              Restaurar seleccion
            </Typography>
          </Button>
        )}
      </Box>
      <Box component={"section"} className={classes.container_content}>
        {/* render view */}
        {loading ? (
          <Box display={"flex"} justifyContent={"center"} marginTop={2}>
            <CircularProgress />
          </Box>
        ) : (
          <List className={classes.samples_list}>
            {products?.map((p) => {
              const { _id, logo, name, molecule, concentration, specialties } =
                p;
              const toAssign = !!productsSelected?.[_id];

              return (
                <ListItem
                  key={_id}
                  className={classes.samples_list_item}
                  style={{ flexDirection: "column", alignItems:'stretch' }}
                  button
                  onClick={() => {
                    changeProductsSelected({
                      data: {
                        productId: _id,
                        name,
                        molecule,
                        concentration,
                        specialties,
                      },
                    });
                  }}
                  data-state={toAssign ? "active" : ""}
                  component={"li"}
                >
                  <Box display={"flex"}>
                    <ListItemAvatar>
                      <Avatar variant="rounded" src={logo} />
                    </ListItemAvatar>
                    <Box
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        width: "80%",
                      }}
                    >
                      <Box
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          wordBreak: "break-all",
                        }}
                      >
                        <ListItemText primary={name} />
                        <Typography
                          variant="caption"
                          style={{ marginTop: "-8px" }}
                        >{`${molecule} - ${concentration}`}</Typography>
                      </Box>
                      {toAssign && (
                        <Icon
                          style={{
                            display: "flex",
                            position: "absolute",
                            top: "9%",
                            right: "3%",
                            width: "auto",
                            height: "auto",
                          }}
                        >
                          <CheckCircleOutlineRoundedIcon
                            fontSize="small"
                            color="secondary"
                          />
                        </Icon>
                      )}
                    </Box>
                  </Box>
                  <Box style={{ display: "flex", flexWrap:'wrap' }}>
                    {specialties.map((s) => (
                      <Chip
                        size="small"
                        variant="outlined"
                        style={{ fontSize: "0.8rem" }}
                        label={s.name}
                        key={`${_id}-${s._id}`}
                      />
                    ))}
                  </Box>
                </ListItem>
              );
            })}
          </List>
        )}
        {products?.length === 0 && !loading && (
          <Box
            style={{
              display: "flex",
              padding: "12px 8px",
              backgroundColor: "#ffa50038",
            }}
          >
            <Typography>Usted no posee muestras que pueda asignar.</Typography>
          </Box>
        )}
      </Box>
      <Box display="flex" justifyContent={"end"}>
        <TablePagination
          labelRowsPerPage="Cant."
          component="div"
          count={count}
          onRowsPerPageChange={(e) => {
            changePaginationProducts({
              action: "LIMIT",
              data: { limit: e.target.value },
            });
          }}
          onPageChange={(e, page) =>
            changePaginationProducts({
              action: "PAGE",
              data: { page: page + 1 },
            })
          }
          page={page - 1}
          rowsPerPage={limit}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Box>
    </>
  );
}
const useStyles = makeStyles((theme) => ({
  container_content: {
    flex: 1,
    maxHeight: "40vh",
    overflowY: "auto",
  },
  searchBar: {
    marginTop: 8,
    marginBottom: 8,
    "& fieldset": {
      borderRadius: 24,
    },
  },
  samples_list: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill,minmax(260px,1fr))",
    gap: theme.spacing(2),
  },
  samples_list_item: {
    borderRadius: theme.spacing(2),
    paddingBlock: theme.spacing(1.5),
    backgroundColor: `hsl(from ${theme.palette.primary.light} h s calc(l + 55))`,
    boxShadow: theme.shadows[1],
    '&[data-state="active"]': {
      outline: `2px solid ${theme.palette.secondary.main}`,
      outlineOffset: "-2px",
    },
  },
  restore_selection: {
    padding: 4,
    height: 35,
    display: "flex",
    alignItems: "center",
    width: "auto",
    borderRadius: 24,
    [theme.breakpoints.down("sm")]: {
      "& span[data-label='label']": {
        display: "none",
      },
    },
  },
}));
